import React from "react";
import { validateFields } from "../../../../common/components/FieldValidator/validateFields";
import { isBusinessNameHidden } from "../../../../common/hooks/useBusinessNameVisibility";
import {
  AppConstants,
  conceptCodes,
  sharedStateKeys,
} from "../../../../constants/constant";
import {
  Element,
  Errors,
  FormValues,
  SchedulerConfig,
  Step,
  ValidateOfferParams,
} from "../../../../Interface/scheduler/form";
import {
  validateOffer,
  fetchCESOffers,
  fetchFranchiseLookupByServiceAddress,
} from "../../../../service/apiService";
import { sessionStorageUtils } from "../../../../utility/utility";
import { BookingApiCallHandler } from "../Helper/BookingApiCallHandler";
import { landingPageOfferSource } from "../../../quotation/Constants/constants";

const validateAllSteps = (
  currentStepConfig: Step,
  formValues: FormValues,
  setErrors: React.Dispatch<React.SetStateAction<Errors>>,
  errors: Errors,
) => {
  return currentStepConfig?.elements?.every((element: Element) => {
    if (element.type === "form") {
      // Filter out businessname field if it should be hidden
      const filteredFields = (element.fields || []).filter(
        (field) => !isBusinessNameHidden(field.id, formValues),
      );
      return validateFields(
        filteredFields ?? [],
        formValues,
        setErrors,
        "",
        errors,
      );
    }
    return true;
  });
};

interface HandleContactUsSingleStepParams {
  formValues: FormValues;
  formConfig: SchedulerConfig;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setFormValues: React.Dispatch<React.SetStateAction<FormValues>>;
  handleNonServicableZipcodes: () => Promise<void>;
  setMakeAnotherCall: React.Dispatch<React.SetStateAction<boolean>>;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setTechnicalModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}
const handleContactUsSingleStep = async ({
  formValues,
  formConfig,
  setIsLoading,
  setFormValues,
  handleNonServicableZipcodes,
  setMakeAnotherCall,
  setIsModalOpen,
  setTechnicalModalOpen,
}: HandleContactUsSingleStepParams) => {
  setIsLoading(true);
  try {
    const res = await fetchFranchiseLookupByServiceAddress(
      formValues?.zipcode as string,
      formConfig?.conceptid,
    );
    setIsLoading(false);
    if (res && res.length > 0) {
      sessionStorage.setItem("bookingLeadSent", "true");
      if (typeof window?.localizationHelper === "function") {
        window?.localizationHelper(res);
      }
      const { franchiseId, licenseNumber, franchiseWebLocationId } = res[0];
      setFormValues((prevValues) => ({
        ...prevValues,
        franchiseId,
        licenseNumber,
        franchiseWebLocationId,
        conceptID: formConfig?.conceptid,
      }));
    } else if (res && res.length === 0) {
      handleNonServicableZipcodes();
      return;
    }
  } catch (error: any) {
    setIsLoading(false);
    if (error?.status === 400) {
      setMakeAnotherCall(true);
      setIsModalOpen(true);
    } else {
      setMakeAnotherCall(false);
      setIsModalOpen(false);
      setTechnicalModalOpen(true);
    }
    console.error("Error fetching franchise lookup:", error);
    return;
  }
};

function maybeAddWdgNonPriciableSkuId(
  updatedValues: FormValues,
  skuId: string,
): FormValues {
  if (skuId !== "") {
    return { ...updatedValues, service: skuId };
  }
  return updatedValues;
}

async function pauseLeadTriggerBookingAPI(
  nextStep: Step | undefined,
  updatedValues: FormValues,
  state: Record<string, any>,
  formConfig: SchedulerConfig,
  setTechnicalModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
  makePauseLeadBookingCall: boolean,
) {
  const shouldTriggerBookingAPI =
    nextStep?.subTitle !== "" &&
    !state[sharedStateKeys.BOOK_AN_INSPECTION_ENABLE];

  if (!shouldTriggerBookingAPI) return;

  const currentZip = updatedValues.zipcode;
  const savedZip = sessionStorageUtils.getItem("zipcodeForPauseLead");
  const needsPauseLeadBookingCall =
    makePauseLeadBookingCall || savedZip !== currentZip;

  let bookingResult = null;
  if (needsPauseLeadBookingCall) {
    sessionStorageUtils.setItem("zipcodeForPauseLead", currentZip);
    bookingResult = await BookingApiCallHandler(
      formConfig,
      updatedValues,
      setTechnicalModalOpen,
      { ...state, [sharedStateKeys.IS_PAUSE_LEAD]: true },
      true,
    );
  }
  return bookingResult;
}

function addSVGIcon(errors: Errors, iconElement: string) {
  const addIconToElements = () => {
    const errorElements = document.querySelectorAll(".error-message");
    errorElements.forEach((element) => {
      const existingSvg = element.querySelector("svg");
      if (!existingSvg && Object.keys(errors).length > 0) {
        const svg = document.createElementNS(
          "http://www.w3.org/2000/svg",
          "svg",
        );
        svg.setAttribute("width", "16");
        svg.setAttribute("height", "16");
        svg.setAttribute("viewBox", "0 0 24 24");
        svg.setAttribute("fill", "red");
        const use = document.createElementNS(
          "http://www.w3.org/2000/svg",
          "use",
        );
        use.setAttributeNS(
          "http://www.w3.org/1999/xlink",
          "xlink:href",
          iconElement,
        );
        svg.appendChild(use);
        element.insertBefore(svg, element.firstChild);
        element.classList.add("error-icon-added");
      }
    });
  };
  addIconToElements();
  const observer = new MutationObserver(() => {
    addIconToElements();
  });
  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
  setTimeout(() => observer.disconnect(), 5000);
}

function getStepsToRemove(formConfig: SchedulerConfig) {
  if (formConfig?.steps?.length == 4) {
    return 2;
  } else if (formConfig?.steps?.length === 3) {
    return 1;
  } else {
    return 0;
  }
}

const handleOfferValidation = async ({
  currentStepConfig,
  formConfig,
  formValues,
  setFormValues,
  validateCESOffer,
}: ValidateOfferParams): Promise<boolean> => {
  if (currentStepConfig?.steptype !== "how-we-can-reach") {
    return true;
  }
  const selectedOffer = sessionStorage.getItem("offerDetails");
  if (!selectedOffer) return true;
  try {
    const offer = JSON.parse(selectedOffer);
    const couponCode = offer.couponcode || offer.couponCode;
    if (
      !offer ||
      !couponCode ||
      !validateCESOffer ||
      offer?.source === landingPageOfferSource ||
      offer?.globalofferenabled
    )
      return true;

    const validationResult = await validateOffer(
      formConfig.conceptid,
      couponCode,
      formValues.franchiseWebLocationId,
      formValues.serviceCategoryID,
      formValues.validate_offers_with_service_categories,
      formValues?.serviceScenarioType?.serviceId,
    );

    if (!validationResult?.success) {
      setFormValues((prevValues) => ({
        ...prevValues,
        offerValidationFailed: true,
      }));
      return false;
    } else if (validationResult?.isCouponApplicable === false) {
      setFormValues((prevValues) => ({
        ...prevValues,
        offerValidationFailed: true,
        isCouponNotApplicable: true,
      }));
      return false;
    } else {
      setFormValues((prevValues) => ({
        ...prevValues,
        offerValidationFailed: false,
        isCouponNotApplicable: false,
      }));
      return true;
    }
  } catch (error) {
    console.error("Error validating offer:", error);
    return true;
  }
};

function canMoveToNextStep(
  moveToNextPage: boolean,
  canMoveToNextpage: boolean,
  stepType: string,
) {
  if (stepType === AppConstants.REQUEST_TIME_TYPE) {
    return moveToNextPage && canMoveToNextpage;
  }
  return true;
}

async function attachOffer(
  formConfig: SchedulerConfig,
  formValues: FormValues,
  cesOfferEnabled: boolean | undefined,
) {
  const sessionOffer = sessionStorage.getItem("offerDetails");
  let validSessionOffer = sessionOffer ? JSON.parse(sessionOffer) : null;

  try {
    if (cesOfferEnabled && !validSessionOffer) {
      const result = await fetchCESOffers(formValues, true);
      if (result?.[0]) {
        validSessionOffer = { ...result[0], isCesGlobal: true };
      }
    } else if (
      !validSessionOffer &&
      formConfig?.globalOffer?.globalofferenabled &&
      typeof cesOfferEnabled === "boolean" &&
      !cesOfferEnabled
    ) {
      validSessionOffer = formConfig?.globalOffer ?? null;
    }
  } catch (error) {
    console.error("Error fetching CES offers:", error);
    validSessionOffer = null;
  }
  if (validSessionOffer)
    sessionStorage.setItem("offerDetails", JSON.stringify(validSessionOffer));
  return validSessionOffer;
}

const handleOfferRemoval = (
  formConfig: SchedulerConfig,
): { shouldRemoveOffer: boolean } => {
  const currentOffer = sessionStorage.getItem("offerDetails");
  if (!currentOffer) {
    return { shouldRemoveOffer: true };
  }

  const offer = JSON.parse(currentOffer);
  const isGlobalOffer =
    offer.globalofferenabled ||
    (formConfig?.globalOffer?.globalofferenabled &&
      offer.couponcode === formConfig.globalOffer.couponcode);
  const isLandingPageOffer = offer.source === landingPageOfferSource;
  const isCesGlobal = offer.isCesGlobal === true;
  return {
    shouldRemoveOffer: !isGlobalOffer && !isLandingPageOffer && !isCesGlobal,
  };
};

const getBannerPosition = (displayTop: boolean): React.CSSProperties => {
  if (displayTop) return {};
  else return { display: "flex", flexDirection: "column-reverse" };
};

const isModifierFlowCalendarRequired = (
  formConfig: SchedulerConfig,
  modifierId: string | null,
): boolean => {
  const conceptCode = formConfig?.conceptCode;
  if (
    conceptCode !== conceptCodes.MDG_CONCEPT_CODE &&
    conceptCode !== conceptCodes.PLG_CONCEPT_CODE
  ) {
    return false;
  }

  if (!modifierId) {
    return false;
  }

  const isAutoFlow =
    modifierId !== AppConstants.RESIDENTIAL_TEXT?.toLowerCase() &&
    modifierId !== AppConstants.COMMERCIAL_TEXT;

  if (isAutoFlow) {
    return !formConfig?.steps?.some(
      (step) => step.steptype === AppConstants.VEHICLE_INFORMATION_SECTION,
    );
  } else {
    return !formConfig?.steps?.some(
      (step) => step.steptype === AppConstants.HOW_WE_CAN_REACH,
    );
  }
};

export {
  validateAllSteps,
  handleContactUsSingleStep,
  maybeAddWdgNonPriciableSkuId,
  pauseLeadTriggerBookingAPI,
  addSVGIcon,
  getStepsToRemove,
  handleOfferValidation,
  canMoveToNextStep,
  attachOffer,
  handleOfferRemoval,
  getBannerPosition,
  isModifierFlowCalendarRequired,
};
